/**
 * Cadastro e séries das estações meteorológicas, publicados pela máquina do
 * agendador (`AmaView-dados/agendador/meteo`, a cada 5 min).
 *
 * O navegador não fala com as fontes: o nó WIS2 do INMET responde com
 * `Access-Control-Allow-Origin` duplicado (o CORS quebra), e o arquivo de
 * METAR da NOAA não tem CORS e só traz a observação mais recente. A máquina
 * junta as 48 h das duas e publica dois JSON pequenos com CORS `*`.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { lonLatToMap } from '../map/projection';
import { CHAVES } from './leitura';
import type { Chave, Dados, Estacao, Leitura, SerieHoraria, SerieMetar } from './types';

/** Onde a máquina publica. `VITE_METEO_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_METEO = (import.meta.env.VITE_METEO_URL ?? 'https://147.15.84.134/meteo').replace(/\/$/, '');

export const METEO_ATRIBUICAO =
  'Estações: <a href="https://portal.inmet.gov.br/" target="_blank" rel="noopener">INMET</a> (WIS2/OMM) · METAR: <a href="https://aviationweather.gov/" target="_blank" rel="noopener">NOAA AWC</a>';

/** Página da estação: a tabela do INMET, ou o METAR decodificado na AWC. */
export function urlDaFonte(e: Pick<Estacao, 'rede' | 'cod'>): string {
  return e.rede === 'm'
    ? `https://aviationweather.gov/data/metar/?id=${encodeURIComponent(e.cod)}&hours=48&decoded=1`
    : `https://tempo.inmet.gov.br/TabelaEstacoes/${encodeURIComponent(e.cod)}`;
}

function num(x: unknown): number | null {
  return typeof x === 'number' && Number.isFinite(x) ? x : null;
}

function texto(x: unknown): string | null {
  return typeof x === 'string' && x.trim() ? x.trim() : null;
}

function lerUltima(u: unknown): Leitura | null {
  if (!u || typeof u !== 'object') return null;
  const o = u as Record<string, unknown>;
  const ms = num(o.ms);
  if (ms === null) return null;
  const valores: Partial<Record<Chave, number>> = {};
  for (const k of CHAVES) {
    const v = num(o[k]);
    if (v !== null) valores[k] = v;
  }
  return { ms, valores, wx: texto(o.wx), raw: texto(o.raw) };
}

/** Uma coluna numérica de tamanho `n`; qualquer coisa que não seja número vira `null`. */
function coluna(x: unknown, n: number): (number | null)[] | null {
  if (!Array.isArray(x) || x.length !== n) return null;
  return x.map((v) => num(v));
}

function lerHoraria(cru: unknown, slots: number): SerieHoraria | null {
  if (!cru || typeof cru !== 'object') return null;
  const o = cru as Record<string, unknown>;
  const out: SerieHoraria = {};
  let alguma = false;
  for (const k of CHAVES) {
    // Série com tamanho errado é dado quebrado, não parcial: desenhar a
    // estação na hora errada seria pior que não desenhar a série.
    const c = coluna(o[k], slots);
    if (c) {
      out[k] = c;
      alguma = true;
    }
  }
  return alguma ? out : null;
}

function lerMetar(cru: unknown): SerieMetar | null {
  if (!cru || typeof cru !== 'object') return null;
  const o = cru as Record<string, unknown>;
  if (!Array.isArray(o.ms)) return null;
  const n = o.ms.length;
  const ms = o.ms.map((v) => num(v));
  if (ms.some((v) => v === null)) return null;
  const valores: SerieMetar['valores'] = {};
  for (const k of CHAVES) {
    const c = coluna(o[k], n);
    if (c) valores[k] = c;
  }
  const strs = (x: unknown) => (Array.isArray(x) && x.length === n ? x.map(texto) : new Array<string | null>(n).fill(null));
  const speci = Array.isArray(o.speci) && o.speci.length === n ? o.speci.map((v) => v === 1 || v === true) : new Array<boolean>(n).fill(false);
  // A série vem em ordem; ordenar de novo protege a busca binária do quadro.
  const ordem = ms.map((_, i) => i).sort((a, b) => ms[a]! - ms[b]!);
  const reordenar = <T,>(a: readonly T[]) => ordem.map((i) => a[i]!);
  return {
    ms: reordenar(ms as number[]),
    valores: Object.fromEntries(Object.entries(valores).map(([k, c]) => [k, reordenar(c)])) as SerieMetar['valores'],
    wx: reordenar(strs(o.wx)),
    raw: reordenar(strs(o.raw)),
    speci: reordenar(speci),
  };
}

/**
 * Interpreta os dois arquivos. Estação sem coordenada ou sem código é
 * descartada; o resto do arquivo continua valendo. Lança erro só quando um
 * arquivo inteiro não serve.
 */
export function lerDados(cruEstacoes: unknown, cruSerie: unknown): Dados {
  const e = cruEstacoes as { estacoes?: unknown; gerado?: unknown };
  if (!e || !Array.isArray(e.estacoes)) throw new Error('estacoes.json sem a lista `estacoes`');
  const s = cruSerie as { t0?: unknown; passoMin?: unknown; slots?: unknown; inmet?: unknown; metar?: unknown; gerado?: unknown };
  if (!s || typeof s !== 'object' || !Number.isFinite(s.t0)) throw new Error('serie.json sem `t0`');

  const slots = Math.max(1, Math.round(num(s.slots) ?? 48));
  const t0 = num(s.t0)!;
  const passoMin = num(s.passoMin) ?? 60;
  const inmet = (s.inmet && typeof s.inmet === 'object' ? s.inmet : {}) as Record<string, unknown>;
  const metar = (s.metar && typeof s.metar === 'object' ? s.metar : {}) as Record<string, unknown>;

  const estacoes: Estacao[] = [];
  const vistos = new Set<string>();
  let maisNova: number | null = null;
  for (const cru of e.estacoes as Record<string, unknown>[]) {
    if (!cru || typeof cru !== 'object') continue;
    const rede = cru.k === 'm' ? 'm' : cru.k === 'i' ? 'i' : null;
    const cod = texto(cru.c);
    const lon = num(cru.x);
    const lat = num(cru.y);
    if (!rede || !cod || lon === null || lat === null) continue;
    const chave = `${rede}:${cod}`;
    if (vistos.has(chave)) continue;
    const [mx, my] = lonLatToMap(lon, lat);
    if (!Number.isFinite(mx) || !Number.isFinite(my)) continue;
    vistos.add(chave);

    const ultima = lerUltima(cru.u);
    if (ultima && (maisNova === null || ultima.ms > maisNova)) maisNova = ultima.ms;
    const u = (cru.u ?? {}) as Record<string, unknown>;
    const c24 = num(u.chuva24);
    estacoes.push({
      rede,
      cod,
      wigos: texto(cru.w),
      nome: texto(cru.n) ?? cod,
      uf: texto(cru.uf),
      pais: texto(cru.pais),
      lat,
      lon,
      alt: num(cru.z),
      mx,
      my,
      sinotica: cru.conv === true,
      oscar: cru.oscar === true,
      ultima,
      chuva24: c24 === null ? null : { mm: c24, horas: num(u.chuva24n) ?? 0 },
      horaria: rede === 'i' ? lerHoraria(inmet[cod], slots) : null,
      metar: rede === 'm' ? lerMetar(metar[cod]) : null,
    });
  }

  const gerado = typeof e.gerado === 'string' ? Date.parse(e.gerado) : NaN;
  return { estacoes, t0, passoMin, slots, gerado: Number.isFinite(gerado) ? gerado : null, maisNova };
}

/** Os dois arquivos mudam a cada 5 min: `no-cache` revalida (o servidor responde 304 quando nada mudou). */
export async function carregarDados(signal?: AbortSignal): Promise<Dados> {
  const opcoes: RequestInit = { cache: 'no-cache', mode: 'cors', credentials: 'omit' };
  const json = (nome: string) =>
    fetchWithRetry<unknown>(`${BASE_METEO}/v1/${nome}`, opcoes, { signal, read: (r) => r.json() as Promise<unknown> });
  const [estacoes, serie] = await Promise.all([json('estacoes.json'), json('serie.json')]);
  return lerDados(estacoes, serie);
}
